'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ManualSubscriptionDto } from '@nomiss/sdk';
import {
  blockAdminUser,
  createAdminUser,
  getAdminUser,
  listAdminUsers,
  overrideAdminUserSubscription,
  type AdminUsersListParams,
  type CreateAdminUserInput,
} from '@nomiss/services';

import { adminSubscriptionsQueryKeys } from './use-admin-subscriptions';

export const adminUsersQueryKeys = {
  all: ['admin', 'users'] as const,
  list: (filters: AdminUsersListParams) =>
    [...adminUsersQueryKeys.all, 'list', filters] as const,
  detail: (id: string) => [...adminUsersQueryKeys.all, 'detail', id] as const,
};

export function useAdminUsersList(filters: AdminUsersListParams) {
  const adminUsersListQuery = useQuery({
    queryKey: adminUsersQueryKeys.list(filters),
    queryFn: () => listAdminUsers(filters),
  });

  return { adminUsersListQuery };
}

export function useAdminUserDetail(id: string | undefined) {
  const adminUserDetailQuery = useQuery({
    queryKey: adminUsersQueryKeys.detail(id ?? ''),
    queryFn: () => getAdminUser(id!),
    enabled: Boolean(id),
  });

  return { adminUserDetailQuery };
}

export function useBlockAdminUser() {
  const queryClient = useQueryClient();

  const blockAdminUserMutation = useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) =>
      blockAdminUser(id, blocked),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: adminUsersQueryKeys.all });
      void queryClient.invalidateQueries({
        queryKey: adminUsersQueryKeys.detail(variables.id),
      });
    },
  });

  return { blockAdminUserMutation };
}

export function useOverrideAdminSubscription() {
  const queryClient = useQueryClient();

  const overrideAdminSubscriptionMutation = useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: ManualSubscriptionDto;
    }) => overrideAdminUserSubscription(id, dto),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: adminUsersQueryKeys.detail(variables.id),
      });
      void queryClient.invalidateQueries({ queryKey: adminUsersQueryKeys.all });
      void queryClient.invalidateQueries({
        queryKey: adminSubscriptionsQueryKeys.all,
      });
      void queryClient.invalidateQueries({
        queryKey: adminSubscriptionsQueryKeys.detail(variables.id),
      });
    },
  });

  return { overrideAdminSubscriptionMutation };
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();

  const createAdminUserMutation = useMutation({
    mutationFn: (input: CreateAdminUserInput) => createAdminUser(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminUsersQueryKeys.all });
    },
  });

  return { createAdminUserMutation };
}
